import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { invoicesApi } from '../../services/api';
import { formatNuf } from '../../utils/formatNuf';

/**
 * Dialog per l'invio singolo email con preview modificabile
 */
const SendEmailDialog = ({ open, onClose, invoice, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  
  // Stati modificabili
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  // Carica preview email quando si apre il dialog
  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await invoicesApi.getEmailPreview(invoice.id);
      const data = response.data?.data;
      
      if (!data) {
        throw new Error('Dati preview non validi');
      }

      setPreview(data);
      setToEmail(data.to_email || '');
      setSubject(data.subject || '');
      setBodyHtml(data.body_html || '');
    } catch (err) {
      console.error('Errore caricamento preview:', err);
      setError(err.response?.data?.message || 'Errore nel caricamento della preview email');
    } finally {
      setLoading(false);
    }
  }, [invoice?.id]);

  useEffect(() => {
    if (open && invoice?.id) {
      loadPreview();
    } else {
      // Reset quando si chiude
      setPreview(null);
      setError(null);
      setToEmail('');
      setSubject('');
      setBodyHtml('');
    }
  }, [loadPreview, open, invoice?.id]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      // Invia con dati personalizzati
      return invoicesApi.sendEmail(invoice.id, {
        to_email: toEmail,
        subject: subject,
        body_html: bodyHtml,
      });
    },
    onSuccess: () => {
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Errore durante l\'invio email');
    },
  });

  const handleSend = () => {
    if (!toEmail) {
      setError('Email destinatario obbligatoria');
      return;
    }
    sendMutation.mutate();
  };

  const handleAttachmentAction = async (attachment, mode = 'download') => {
    if (attachment.type === 'invoice_pdf') {
      // no-op, gestito sotto tramite endpoint allegati
    }

    try {
      const response = await invoicesApi.downloadAttachment(invoice.id, attachment.type);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      if (mode === 'preview') {
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        return;
      }

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Errore download allegato:', err);
      setError('Errore nel download dell\'allegato');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{parseFloat(invoice?.f_totale || 0) < 0 ? 'Invia Nota di Credito via Email' : 'Invia Fattura via Email'}</Typography>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon />}>
            Chiudi
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Info fattura */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>{parseFloat(invoice?.f_totale || 0) < 0 ? 'Nota di Credito:' : 'Fattura:'}</strong> {formatNuf(invoice?.sezionale, invoice?.nuf) || '-'} | 
                <strong> Cliente:</strong> {preview?.customer_name || '-'}
              </Typography>
            </Alert>

            {/* Campo destinatario */}
            <TextField
              label="A (Destinatario)"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              fullWidth
              required
              type="email"
              helperText="Email del destinatario"
            />

            {/* Campo oggetto */}
            <TextField
              label="Oggetto"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              required
            />

            {/* Allegati (read-only) */}
            {preview?.attachments && preview.attachments.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Allegati ({preview.attachments.length})
                </Typography>
                <List dense>
                  {preview.attachments.map((att, idx) => (
                    <ListItem 
                      key={idx}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleAttachmentAction(att, 'preview')}
                          >
                            Anteprima
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleAttachmentAction(att, 'download')}
                          >
                            Scarica
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemIcon>
                        <AttachFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={att.filename || `Allegato ${idx + 1}`}
                        secondary={att.description || (att.type === 'invoice_pdf' ? (parseFloat(invoice?.f_totale || 0) < 0 ? 'Nota di Credito PDF' : 'Fattura PDF') : 'Bollettino Postale')}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Divider />

            {/* Corpo messaggio (HTML preview) */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Anteprima Messaggio
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'background.paper',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            </Box>

            {/* Campo corpo modificabile (opzionale - textarea) */}
            <TextField
              label="Corpo Email (HTML)"
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              multiline
              rows={6}
              fullWidth
              helperText="Puoi modificare il corpo dell'email (HTML)"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sendMutation.isPending}>
          Annulla
        </Button>
        <Button
          variant="contained"
          startIcon={sendMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={handleSend}
          disabled={loading || !!error || !toEmail || sendMutation.isPending}
        >
          {sendMutation.isPending ? 'Invio...' : 'Invia'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendEmailDialog;
